import React, { useState, useEffect } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import { ExpiryVehicle, ExpiryKind, CALL_REASONS } from './queries';
import { fmtEpoch, severityColor } from './format';

export type CallLogEntry = {
  note: string;
  /** One of CALL_REASONS. Optional — a call can end without a refusal. */
  reason: string;
  /** Who answered at the customer's end. */
  attendedBy: string;
};

type Props = {
  open: boolean;
  vehicle: ExpiryVehicle | null;
  kind: ExpiryKind;
  busy: boolean;
  onClose: () => void;
  onSubmit: (entry: CallLogEntry) => void;
};

const field = (label: string, value: React.ReactNode) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.68rem', color: '#888', letterSpacing: 0.5 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, wordBreak: 'break-word' }}>
      {value ?? 'N/A'}
    </Typography>
  </Box>
);

/**
 * The detail panel behind the Call button: who to call, what is lapsing, and a
 * note that appends to this vehicle's running call log.
 *
 * Both certificates are shown whichever list you came from — a vehicle whose
 * SIM lapses next month may well have a certificate lapsing the month after,
 * and one call should settle both.
 */
const CallLogDialog: React.FC<Props> = ({ open, vehicle, kind, busy, onClose, onSubmit }) => {
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [attendedBy, setAttendedBy] = useState('');

  // Clear every field whenever a different vehicle is opened, so nothing typed
  // for one customer can be submitted against the next. Resetting the reason
  // matters most: a stale dropdown selection submits silently, unlike a stale
  // note which the agent would see.
  useEffect(() => {
    if (open) {
      setNote('');
      setReason('');
      setAttendedBy('');
    }
  }, [open, vehicle?.vin]);

  if (!vehicle) return null;

  const days = vehicle.daysToExpiry ?? 0;
  const color = severityColor(vehicle.severity);
  const lapsed = days < 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {vehicle.customerName || 'Unknown customer'}
        <Typography component="span" sx={{ color: '#888', fontSize: '0.9rem', ml: 1 }}>
          — {vehicle.vin || 'no VIN'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 2,
            borderRadius: 2, backgroundColor: `${color}14`, border: `1px solid ${color}`,
          }}
        >
          <InfoOutlinedIcon sx={{ color }} />
          <Box>
            <Typography sx={{ fontWeight: 700, color, fontSize: '0.95rem' }}>
              {lapsed
                ? `Lapsed ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`
                : `${days} day${days === 1 ? '' : 's'} to ${kind === 'ais' ? 'certificate' : 'Saarthi Pro'} expiry`}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>
              Renewal is raised by the customer from the FMS application — this panel records the call only.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
          {field('CUSTOMER', vehicle.customerName)}
          {field('PHONE', vehicle.customerMobile)}
          {field('VEHICLE', vehicle.vehicleModel)}
          {field('VIN', vehicle.vin)}
          {field('IMEI', vehicle.imei)}
          {field('ICCID', vehicle.iccid)}
          {field('DEALER', vehicle.dealerName)}
          {field('DEALER CONTACT', vehicle.dealerContactNumber)}
          {field('RTO', vehicle.rtoName || vehicle.registrationState)}
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: '0.7rem', color: '#888', letterSpacing: 0.5, mb: 1 }}>
          CERTIFICATE VALIDITY
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Common Layer (AIS-140)</Typography>
            {vehicle.claExpiresOn ? (
              <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
                Issued {fmtEpoch(vehicle.claIssuedOn)} · Expires {fmtEpoch(vehicle.claExpiresOn)}
                {vehicle.claDaysToExpiry !== null && (
                  <Chip
                    size="small"
                    label={`${vehicle.claDaysToExpiry}d`}
                    sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                  />
                )}
              </Typography>
            ) : (
              <Tooltip title="No certificate issue date on record for this vehicle yet.">
                <Typography sx={{ fontSize: '0.8rem', color: '#999' }}>Not available</Typography>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Saarthi Pro (SIM subscription)</Typography>
            {vehicle.saarthiProExpiryDate ? (
              <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
                Started {fmtEpoch(vehicle.saarthiProStartDate)} · Expires {fmtEpoch(vehicle.saarthiProExpiryDate)}
                {vehicle.saarthiProDaysToExpiry !== null && (
                  <Chip
                    size="small"
                    label={`${vehicle.saarthiProDaysToExpiry}d`}
                    sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                  />
                )}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: '0.8rem', color: '#999' }}>Not available</Typography>
            )}
          </Box>
        </Box>

        {vehicle.lastCallNote && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#888', letterSpacing: 0.5, mb: 1 }}>
              PREVIOUS CALLS
              {vehicle.lastCallAt ? ` — last on ${fmtEpoch(vehicle.lastCallAt)}` : ''}
              {vehicle.lastCallBy ? ` by ${vehicle.lastCallBy}` : ''}
            </Typography>
            {(vehicle.lastCallReason || vehicle.lastCallAttendedBy) && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                {vehicle.lastCallReason && (
                  <Chip
                    size="small"
                    label={vehicle.lastCallReason}
                    sx={{ height: 22, fontSize: '0.72rem', backgroundColor: '#ed6c021A', color: '#ed6c02', border: '1px solid #ed6c02' }}
                  />
                )}
                {vehicle.lastCallAttendedBy && (
                  <Chip
                    size="small"
                    label={`Attended by ${vehicle.lastCallAttendedBy}`}
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.72rem' }}
                  />
                )}
              </Box>
            )}
            <Box
              sx={{
                p: 1.5, borderRadius: 2, backgroundColor: '#f7f7f7', maxHeight: 160,
                overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.8rem', color: '#444',
              }}
            >
              {vehicle.lastCallNote}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: '0.7rem', color: '#888', letterSpacing: 0.5, mb: 1.5 }}>
          THIS CALL
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            select
            label="Reason for not recharging"
            size="small"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText="Leave blank if the customer is going ahead"
          >
            {/* An explicit empty option — without one there is no way back to
                "no reason" once something has been picked. */}
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {CALL_REASONS.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Attended by"
            placeholder="Who picked up the call"
            size="small"
            fullWidth
            value={attendedBy}
            onChange={(e) => setAttendedBy(e.target.value)}
            helperText="Person at the customer's end — your own name is recorded automatically"
          />
        </Box>

        <TextField
          label="Call notes"
          placeholder="Outcome, customer response, next steps..."
          multiline
          minRows={3}
          fullWidth
          size="small"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<PhoneIcon />}
          onClick={() => onSubmit({ note, reason, attendedBy })}
          disabled={busy || !note.trim()}
          sx={{ backgroundColor: '#E60012', '&:hover': { backgroundColor: '#c1000f' } }}
        >
          Log Call
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CallLogDialog;
